/* eslint-disable import/prefer-default-export */
/**
 * Account Submission Utilities
 * Single utility for handling all account form submissions
 */

import type { Dispatch } from '@reduxjs/toolkit';

import { MESSAGE_SUBTYPES, MESSAGE_TYPES } from '../constants/steps';
import type { CopilotState } from '../data-layer/types';
import { addMessage, updateStepFormData } from '../data-store/copilotSlice';
import { showError } from '../services/messageService';

import { hasUserProvidedPrompts } from './promptDetectionUtils';

type AccountStepName =
  | 'CREATE_ACCOUNT'
  | 'UPDATE_ACCOUNT'
  | 'ENABLE_ACCOUNT'
  | 'DISABLE_ACCOUNT'
  | 'REMOVE_ACCOUNT';

/**
 * Show account helper prompt with 3 option buttons
 */
const showAccountHelperPrompt = (
  dispatch: Dispatch,
  stepName: AccountStepName,
  getState: () => unknown
): void => {
  const state = getState() as { copilot?: CopilotState };
  const hasPrompts = hasUserProvidedPrompts(stepName, state.copilot!);

  if (hasPrompts) {
    const providedPrompt = state.copilot?.accountPrompts[stepName] || '';

    dispatch(
      addMessage({
        id: `${stepName.toLowerCase()}-prompt-instructions-${Date.now()}`,
        type: MESSAGE_TYPES.AI_RESPONSE,
        subType: 'promptInstructions',
        timestamp: Date.now(),
        text: `Hey, it seems you've already provided the additional information.
${providedPrompt}
Do you want to continue with above instruction?`,
        buttons: [
          { label: 'Yes', action: 'continue_with_instructions' },
          { label: 'No', action: 'modify_instructions' },
        ],
      })
    );
  } else {
    dispatch(
      addMessage({
        id: `${stepName.toLowerCase()}-helper-${Date.now()}`,
        type: MESSAGE_TYPES.AI_RESPONSE,
        subType: MESSAGE_SUBTYPES.THREE_BUTTONS_HELPER_TEXT,
        timestamp: Date.now(),
        buttons: [
          { label: 'Specify Prompt', action: 'all_account_help_yes' },
          {
            label: 'Upload Browser Recording',
            action: 'all_account_help_yes',
          },
          { label: 'Skip for Now', action: 'all_account_help_no' },
        ],
      })
    );
  }
};

/**
 * Handle all account form submissions
 */
export const handleAccountSubmit = async (
  dispatch: Dispatch,
  stepName: AccountStepName,
  formData: Record<string, unknown>,
  isValid: boolean,
  getState: () => unknown
  // eslint-disable-next-line @typescript-eslint/require-await
): Promise<void> => {
  // Store form data in Redux
  dispatch(updateStepFormData({ stepName, formData }));

  if (!isValid) {
    showError(
      dispatch,
      `Please check ${stepName.toLowerCase().replace('_', ' ')} details`
    );

    return;
  }

  // Show helper prompt (step will be marked completed in success callback)
  showAccountHelperPrompt(dispatch, stepName, getState);
};
